/**
 * Phase-9.1 — Top-level error boundary.
 *
 * Catches render errors anywhere in the route tree so a single broken
 * component (e.g. a missing import in <Profile>) never blanks the whole app.
 * Shows a fallback card with a reload button and surfaces the error to the
 * console so devs can still diagnose.
 */
import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught render error:", error, info?.componentStack);
  }

  handleReload = () => {
    // Soft reset first; if user clicks again we hard-reload.
    if (this.state.error) {
      this.setState({ error: null });
      // If the route remounts cleanly we're done. Otherwise force a full reload.
      setTimeout(() => {
        if (this.state.error) window.location.reload();
      }, 50);
    } else {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error?.message || String(this.state.error);

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-[var(--bg,#0a0a0a)] text-zinc-100 flex items-center justify-center px-6"
      >
        <div className="max-w-md w-full rounded-2xl border border-white/10 bg-zinc-950/70 backdrop-blur p-8 text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent,#10b981)] mb-3">
            something went wrong
          </div>
          <h1 className="text-3xl font-black mb-2">We hit a snag.</h1>
          <p className="text-sm text-zinc-400 mb-6">
            A page failed to render. The rest of the app is still alive — try reloading.
          </p>
          <pre
            data-testid="error-boundary-message"
            className="text-[11px] text-zinc-500 bg-black/40 rounded-lg p-3 mb-6 overflow-auto max-h-40 text-left"
          >
            {msg}
          </pre>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="px-4 py-2 rounded-full bg-[var(--accent,#10b981)] text-black font-bold text-sm hover:opacity-90"
            >
              Reload
            </button>
            <a
              href="/"
              data-testid="error-boundary-home"
              className="px-4 py-2 rounded-full border border-white/10 text-zinc-200 font-bold text-sm hover:bg-white/5"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
